/**
 * OpenBro offer facts.
 *
 * The landing reads only this file for prices, proof, capacity, and duration.
 * Leave a field null when it is unknown: the matching UI is hidden.
 * Never invent followers, members, reviews, spots, or a previous price.
 */

const offer = {
  /**
   * Prix du mentorat (promotion), jusqu'à launchEndsAt s'il y en a un, dans les trois devises. Doit correspondre à
   * LAUNCH_CENTS dans netlify-proxy/worker.js, qui choisit la devise selon le pays du visiteur (lib/pricing.js affiche la même).
   * Historique : lancement 29 € (99 € barré), 29 $, 39 $ CA jusqu'au 11/10/2026, puis 99 €, 49 $, 69 $ CA.
   * Depuis le 28/10/2026 (choix de Gaut) : promotion −50 % sans date de fin, 49 € avec 99 € barré, 49 $, 69 $ CA.
   */
  price: 49,
  usdPrice: 49,
  cadPrice: 69,
  /** Après launchEndsAt (AFTER_LAUNCH_CENTS dans le Worker). En euros, c'est originalPrice. */
  usdPriceAfter: 49,
  cadPriceAfter: 69,
  /**
   * Prix en euros barré à côté du prix de promotion (et prix normal après launchEndsAt, s'il y en a un).
   * Null cache le barré et le pourcentage. Jamais montré en dollars (99 $ n'a jamais été facturé).
   */
  originalPrice: 99,
  /**
   * Fin de la promotion (heure de Paris), ou null : promotion sans date de fin, barré sans date ni compte à rebours.
   * Le Worker applique la même règle au checkout (LAUNCH_END dans netlify-proxy/worker.js, Infinity quand c'est null) :
   * garder les deux alignées.
   */
  launchEndsAt: null,
  /**
   * /eat (plan alimentaire) : le même chiffre partout.
   * `launch` jusqu'à launchEndsAt, puis `after` (prix normal). Doit correspondre à EAT_LAUNCH_CENTS / EAT_CENTS dans netlify-proxy/worker.js.
   * `after` était à 99 du 12 au 28 octobre 2026 ; depuis le 28 octobre 2026, 29 partout (choix de Gaut), donc ni barré ni compte à rebours.
   * Devise par pays : Canada → CAD, États-Unis → USD, ailleurs → EUR.
   */
  eatPrices: {
    launch: { usd: 29, cad: 29, eur: 29 },
    after: { usd: 29, cad: 29, eur: 29 },
  },
  /** Combined TikTok + Instagram followers. */
  socialFollowers: null,
  /** Current paying members. Shown instead of followers when set. */
  payingMembers: null,
  /**
   * Reviews. All three fields are required or the stars stay hidden.
   * Shape: { rating: number, count: number, source: string }
   */
  reviews: null,
  /** Photo URLs of real members who agreed to appear. Empty hides the avatars. */
  memberAvatars: [],
  /** People accepted per month. Null hides the capacity sentence. */
  monthlyCapacity: null,
  /** Spots left this month. Null hides the remaining line and the progress bar. */
  placesRemaining: null,
  /** Call length in minutes. Null drops “{durée} min” from the copy. */
  callDurationMinutes: null,
  /** External payment URL. Null keeps the current “Commencer maintenant” checkout. */
  paymentUrl: null,
  /**
   * Bust portrait, facing camera, eyes visible, no sunglasses, blended into the earth.
   * Null: the hero uses the earth background only.
   */
  portraitSrc: null,
};

export default offer;

export const HERO_IMAGE = '/hero-earth.jpg';

/** Temporary call-card photo. Sunglasses, eyes hidden. Replace with the portrait brief. */
export const CALL_PORTRAIT = '/v3-call.jpg';

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isFilled(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'number' && Number.isNaN(value)) return false;
  return true;
}

/**
 * @param {number} value
 * @param {'fr' | 'en'} lang
 * @returns {string}
 */
export function formatCount(value, lang) {
  return new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'fr-FR').format(value);
}

/**
 * @param {number} amount
 * @returns {string}
 */
export function euroAmount(amount) {
  return `${amount}\u00A0€`;
}

/**
 * @param {number} value
 * @returns {string}
 */
export function percentLabel(value) {
  return `${value}\u00A0%`;
}

/**
 * @param {'fr' | 'en'} lang
 * @returns {string | null}
 */
export function reviewLine(lang) {
  const review = offer.reviews;
  if (!review || !isFilled(review.rating) || !isFilled(review.count) || !isFilled(review.source)) {
    return null;
  }
  const rating = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(Number(review.rating));
  const count = formatCount(review.count, lang);
  const noun = lang === 'en' ? 'reviews' : 'avis';
  return `${rating}/5 · ${count} ${noun} · ${review.source}`;
}

/**
 * Real fill, from capacity and spots left. Null when either fact is missing.
 * @returns {number | null}
 */
export function placesFillPercent() {
  if (!isFilled(offer.placesRemaining) || !isFilled(offer.monthlyCapacity)) return null;
  const capacity = Number(offer.monthlyCapacity);
  const remaining = Number(offer.placesRemaining);
  if (capacity <= 0) return null;
  const taken = Math.min(capacity, Math.max(0, capacity - remaining));
  return Math.round((taken / capacity) * 100);
}

/**
 * @returns {number | null}
 */
export function shownOriginalPrice() {
  if (!isFilled(offer.originalPrice)) return null;
  if (Number(offer.originalPrice) <= Number(offer.price)) return null;
  return Number(offer.originalPrice);
}

/**
 * @returns {number | null}
 */
export function discountPercent() {
  const previous = shownOriginalPrice();
  if (previous === null) return null;
  // Arrondi en dessous, comme lib/pricing.js : jamais plus que la vraie remise (49 au lieu de 99 = « −50 % »).
  return Math.floor(((previous - Number(offer.price)) / previous) * 100);
}

/**
 * @returns {number | null}
 */
export function callMinutes() {
  if (!isFilled(offer.callDurationMinutes)) return null;
  return Number(offer.callDurationMinutes);
}
